import React, { useState, useMemo, useEffect } from "react";
import { data, useNavigate, useParams } from "react-router-dom";
import { notification } from "antd";
import { filmStore } from "@/store/film";
import { getResponseErrorMessage } from '@/utils/api-service'

import api from "@/api/index";

import CardContent from "../components/card/CardContent";
import FilmMainInfo from "../components/film/FilmMainInfo";
import VideoCategories from "@/components/categories/VideoCategories";
import FilmVideoFiles from "@components/film/FilmVideoFiles";
import AppHeader from "../components/header/AppHeader";

import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";

import "../styles/film-create.scss";

function FilmCreate() {
  const [film, setFilm] = useState({});
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [voiceoversForRemove, setVoiceoversToRemove] = useState([]);
  const [isShowCategories, setIsShowCategories] = useState<boolean>(true);

  const navigation = useNavigate();

  const { id } = useParams();

  const onCancel = () => {
    navigation("/");
  };

  const isDisabledSave = useMemo(() => {
    if(!film.name || isLoading) {
      return true
    }

    return false
  }, [film, categories]);

  const handleChangeFilm = (option, value) => {
    setFilm((prev) => ({
      ...prev,
      [option]: value,
    }));
  };

  const handleUpdateMainOption = (key:string, value: any) => {
    setFilm({
      ...film,
      [key]: value
    })
  }

  const updateAllOptions = (data) => {
    setFilm({
      ...film,
      ...data
    })

    setCategories(data.categories)
    updateCategories()
  }

  const updateCategories = () => {
    setIsShowCategories(false)

    setTimeout(() => {
      setIsShowCategories(true)
    }, 1000)
  }

  const updateRemoveVoiceovers = (item) => {
    setVoiceoversToRemove((prev) => [...prev, item])
  }

  const onSaveFilm = async () => {
    try {
      if(isLoading) {
        return false;
      }

      setIsLoading(true)

      let listNew = [];
      let listOld = [];
      let listDelete = [];
      let listEdit = []

      for(const item of filmStore.listVoiceovers) {
        let action = null;

        if(item.isDeleted || item.isEdit) {
          action = 'delete';
          listDelete.push(item)
        }

        if(item.isEdit) {
          action = 'edit';
          listEdit.push(item)

        } else if(item.isNew) {
          action = 'new';
          listNew.push(item)
        }

        if(!action) {
          listOld.push(item);
        }
      }

      listDelete = [...voiceoversForRemove, listDelete];

      if(!Array.isArray(filmStore.listVoiceovers) || !filmStore.listVoiceovers.length) {
        notification.warning({
          message: 'Выберите хотя бы 1 видеофайл'
        })
        return
      }

      if(!id) {
        listOld = listNew.slice()
      }

      const data = {
        title_original: film.name_original,
        title: film.name,
        description: film.description,
        release_year: film.year ? film.year : (film.release_year ? film.release_year : 0),
        kinopoisk_id: filmStore?.film?.kinopoisk_id ? filmStore.film.kinopoisk_id : null,
        imdb_id: filmStore?.film?.imdb_id ? filmStore.film.imdb_id : null,
        categories: categories,
        isActive: film.isActive ?? true,
        trailer_url: film.trailer_url || null,
        tags: [],
        voiceovers: listOld.map((item) => {
          return {
            "voiceover_id": item.voiceover_id,
            "video_file": item.video_file ? item.video_file : 'file.mp4',
            "has_internal_ads": item.has_internal_ads,
            isActive: item.isActive ?? undefined,
            ...(item.audio_track_index !== null && item.audio_track_index !== undefined
              ? { audio_track_index: item.audio_track_index }
              : {}),
            ...(item.delete_source ? { delete_source: true } : {}),
          }
        })
      };

      let filmId = id;

      if(!id) {
        await api.movie.create(data);
      } else {
        await api.movie.edit(data, id)
      }


      if(id) {
        await addVoiceovers(listNew, filmId);
        await removeVoiceOvers(filmId, listDelete)
        await editVoiceoversFilm(filmId, listEdit)
      }

      notification.success({
        message: "Фильм сохранен",
        description: id
          ? "Данные фильма успешно изменены"
          : "Фильм успешно добавлен в базу данных",
      });

      filmStore.clearVoiceovers()
      filmStore.setFilm({})

      if(!id) {
        navigation("/")
      } else {
        fetchFilm();
      }

    } catch (err) {
      console.error(err)

      const messageFormatter = getResponseErrorMessage(err, 'Ошибка при сохранении')
      notification.error({
        message: messageFormatter
      })

    } finally {
      setIsLoading(false)
    }
  };

  const removeVoiceOvers = async (filmId, list) => {
    for(const voiceoverRemove of list) {
      if(voiceoverRemove.id && voiceoverRemove.video_id) {
        await api.movie.removeVoiceoverFromFilm(filmId, voiceoverRemove.id, voiceoverRemove.video_id)
      }
    }
  }

  const editVoiceoversFilm = async (filmId, list) => {
    for(const item of list) {
      const reqData: Record<string, unknown> = {
          "voiceover_id": item.voiceover_id,
          "video_file": item?.video_file || '',
          "has_internal_ads": item.has_internal_ads,
          "isActive": item.isActive ?? item.active ?? true
      };
      if (item.exclusive_days !== undefined) {
        reqData.exclusive_days = item.exclusive_days;
      }
      if (item.audio_track_index !== null && item.audio_track_index !== undefined) {
        reqData.audio_track_index = item.audio_track_index;
      }
      if (item.delete_source) {
        reqData.delete_source = true;
      }
      await api.movie.editeVoiceoverFromFilm(filmId, item.id, item.video_id, reqData)
    }
  }

  const addVoiceovers = async (listNew, filmId) => {
    for(const voiceoverNew of listNew) {
        await api.movie.addVoiceover(filmId, {
          "voiceover_id": voiceoverNew.voiceover_id,
          "video_file": voiceoverNew.video_file,
          "has_internal_ads": voiceoverNew.has_internal_ads,
          isActive: voiceoverNew.isActive,
          exclusive_days: voiceoverNew.exclusive_days || 0,
          ...(voiceoverNew.audio_track_index !== null && voiceoverNew.audio_track_index !== undefined
            ? { audio_track_index: voiceoverNew.audio_track_index }
            : {}),
          ...(voiceoverNew.delete_source ? { delete_source: true } : {}),
        })
    }
  }

  const handleChangeCategories = (category: string) => {
    const list = categories;
    const index = list.indexOf(category);

    if (index > -1) {
      list.splice(index, 1);
    } else {
      list.push(category);
    }

    setCategories(list)
  };

  const fetchFilm = async () => {
    try {
      const response = await api.movie.get(id);
      const data = response.data.data;

      let responseCategories= [];
      if(Array.isArray(response.data.data.categories)) {
        responseCategories = response.data.data.categories.map((item) => {
          return item.id
        })
      }

      data.year = data.release_year
      data.isActive = data.status_id === 1 ? true : false

      setFilm(data || {});
      setCategories(responseCategories);

      const voiceovers = response?.data?.data?.voiceovers.map((voiceover) => {
        return {
          ...voiceover,
          voiceover_id: voiceover.id
        }
      })

      filmStore.setVoiceover(voiceovers)
      filmStore.setFilmOption('kinopoisk_id', data.kinopoisk_id || null)
      filmStore.setFilmOption('imdb_id', data.imdb_id || null)

    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const pageTitle = useMemo(() => {
    if(id) {
      return 'Редактирование фильма'
    }

    return 'Добавление фильма'
  }, [])

  useEffect(() => {
    if (id) {
      fetchFilm();

    } else {
      setIsLoading(false);
    }
  }, [id]);

  return (
    <div className="film-create app-page">
      <AppHeader title={pageTitle} link="/" />

      {isLoading && <Spin size="large" indicator={<LoadingOutlined />} />}

      {!isLoading && (
        <div className="app-container">
          <div className="film-create__content">
            <CardContent title="Основная информация">
              <FilmMainInfo changeFilm={handleChangeFilm} film={film} updateOption={handleUpdateMainOption} updateAllOptions={updateAllOptions} />
            </CardContent>

            <CardContent title="Категории">
              {
                isShowCategories && <VideoCategories onChange={handleChangeCategories} list={categories} />
              }
            </CardContent>

            <div>
              <FilmVideoFiles updateRemoveList={updateRemoveVoiceovers} movieId={id} onVoiceoversChanged={fetchFilm} />
            </div>

            <div className="film-create__control app-footer-control">
              <button className="app-btn secondary" onClick={onCancel}>
                Отмена
              </button>
              <button
                className={isDisabledSave ? 'app-btn primary disabled' : 'app-btn primary'}
                onClick={onSaveFilm}
                disabled={isDisabledSave}
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FilmCreate;
